import { skipToken, useMutation, useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useParams, useSearchParams } from "react-router";
import { queryClient, trpc, trpcErrorCode } from "../../app/trpc.js";
import { Badge, Button, buttonClass, Card, Chip, Notice, SectionTitle, Spinner } from "../../ui/index.js";
import { PublicShell, usePublicLocale } from "../booking-page/index.js";
import { addDays, dayKey, fmtDay, fmtMoney, fmtTime } from "../cabinet/index.js";

const LATE_OPTIONS = [5, 10, 15, 20, 30, 45] as const;
type LateMinutes = (typeof LATE_OPTIONS)[number];
type Mode = "view" | "reschedule" | "cancel" | "late";

/**
 * «Моя запись» `/my/:token`: итог визита, «добавить в календарь» (.ics), перенос и отмена по правилам салона
 * (поздняя отмена принимается с пометкой), «опаздываю на N минут»: мастер и владелец получают push.
 * Ссылку получает клиент со страницы записи, а после записи в Instagram от бота.
 */
export function MyBookingPage() {
  const { t, i18n } = useTranslation();
  const { token = "" } = useParams();
  const [params] = useSearchParams();
  const lang = i18n.language;
  const view = useQuery(trpc.onboarding.public.my.get.queryOptions({ token, locale: lang as "sr-Latn" | "en" | "ru" }, { retry: false }));
  const [mode, setMode] = useState<Mode>("view");
  const [notice, setNotice] = useState<{ kind: "success" | "error" | "info"; text: string } | null>(null);
  const tz = view.data?.salon.timezone ?? "Europe/Belgrade";
  const today = useMemo(() => dayKey(new Date(), tz), [tz]);
  const [day, setDay] = useState<string | null>(null);
  const activeDay = day ?? today;
  const [picked, setPicked] = useState<Date | null>(null);
  usePublicLocale(view.data?.salon.locale);

  const slots = useQuery(trpc.onboarding.public.my.slots.queryOptions(mode === "reschedule" ? { token, day: activeDay } : skipToken));
  const openMode = (m: Mode) => {
    setNotice(null);
    setMode(m);
  };
  const pickDay = (d: string) => {
    setDay(d);
    setPicked(null);
  };
  const refresh = () => queryClient.invalidateQueries(trpc.onboarding.public.my.pathFilter());
  const fail = (e: unknown) => {
    const code = trpcErrorCode(e);
    setNotice({
      kind: "error",
      text:
        code === "slot_taken" || code === "outside_hours"
          ? t("my.slotTaken")
          : code === "TOO_MANY_REQUESTS"
            ? t("book.tooMany")
            : code === "too_late_to_cancel"
              ? t("my.tooLate")
              : t("common.error"),
    });
  };
  const reschedule = useMutation(
    trpc.onboarding.public.my.reschedule.mutationOptions({
      onSuccess: () => {
        setMode("view");
        setPicked(null);
        setNotice({ kind: "success", text: t("my.rescheduled") });
        void refresh();
      },
      onError: fail,
    }),
  );
  const cancel = useMutation(
    trpc.onboarding.public.my.cancel.mutationOptions({
      onSuccess: () => {
        setMode("view");
        setNotice({ kind: "success", text: t("my.cancelled") });
        void refresh();
      },
      onError: fail,
    }),
  );
  const late = useMutation(
    trpc.onboarding.public.my.late.mutationOptions({
      onSuccess: (r) => {
        setMode("view");
        setNotice({ kind: "success", text: r.suggestReschedule ? t("my.lateSentReschedule") : t("my.lateSent") });
        void refresh();
      },
      onError: fail,
    }),
  );

  if (view.isPending) return <Spinner />;
  if (!view.data)
    return (
      <PublicShell>
        <Notice kind="error">{trpcErrorCode(view.error) === "TOO_MANY_REQUESTS" ? t("book.tooMany") : t("my.notFound")}</Notice>
      </PublicShell>
    );
  const b = view.data;
  const statusTone =
    b.status === "confirmed"
      ? "green"
      : b.status === "pending"
        ? "amber"
        : b.status === "cancelled" || b.status === "rejected"
          ? "red"
          : b.status === "held"
            ? "brand"
            : "slate";
  const days = Array.from({ length: b.horizonDays }, (_, i) => addDays(today, i));

  return (
    <PublicShell salonName={b.salon.name} slug={b.salon.slug}>
      {params.get("new") === "1" && b.status !== "cancelled" && <Notice kind="success">{t("my.booked")}</Notice>}
      <h1 className="mt-3 text-2xl font-semibold text-ink">{t("my.title")}</h1>
      <Card className="mt-4 space-y-1.5">
        <div className="flex items-center justify-between gap-2">
          <div className="text-lg font-semibold text-ink">
            {fmtDay(dayKey(b.start, tz), lang, { weekday: "long", day: "numeric", month: "long" })}, {fmtTime(b.start, tz, lang)}
          </div>
          <Badge tone={statusTone} dot>
            {t(`my.status.${b.status}`)}
          </Badge>
        </div>
        <div className="text-ink">{b.services.join(" + ")}</div>
        <div className="text-sm text-ink-2">{t("my.master", { name: b.masterName })}</div>
        <div className="text-sm text-ink-2">{fmtMoney(b.total.amount, lang)}</div>
        {b.salon.address && <div className="text-sm text-ink-2">{b.salon.address}</div>}
        {b.lateCancel && <div className="text-sm text-amber-700">{t("my.lateCancelMark")}</div>}
      </Card>

      {notice && (
        <div className="mt-4">
          <Notice kind={notice.kind}>{notice.text}</Notice>
        </div>
      )}

      {b.status !== "cancelled" && b.status !== "rejected" && (
        <a href={b.icsUrl} className={buttonClass("secondary", "mt-4 w-full")}>
          {t("my.addToCalendar")}
        </a>
      )}
      {b.canChange && <p className="mt-2 text-xs text-ink-3">{t("my.saveLink")}</p>}

      {mode === "view" && (
        <div className="mt-4 space-y-2">
          {b.lateAllowed && (
            <Button className="w-full" variant="secondary" onClick={() => setMode("late")}>
              {t("my.late")}
            </Button>
          )}
          {b.canChange && (
            <>
              <Button className="w-full" variant="secondary" onClick={() => openMode("reschedule")}>
                {t("my.reschedule")}
              </Button>
              <Button className="w-full" variant="ghost" onClick={() => openMode("cancel")}>
                {t("my.cancel")}
              </Button>
            </>
          )}
        </div>
      )}

      {mode === "late" && (
        <Card className="mt-4 space-y-4">
          <p className="text-sm">{t("my.lateQuestion")}</p>
          <div className="flex flex-wrap gap-2">
            {LATE_OPTIONS.map((m) => (
              <Chip key={m} onClick={() => late.mutate({ token, minutes: m as LateMinutes })} disabled={late.isPending}>
                {t("my.minutes", { count: m })}
              </Chip>
            ))}
          </div>
          <p className="text-xs text-ink-3">{t("my.lateTolerance", { count: b.lateToleranceMin })}</p>
          <Button variant="ghost" className="w-full" onClick={() => setMode("view")}>
            {t("common.cancel")}
          </Button>
        </Card>
      )}

      {mode === "cancel" && (
        <Card className="mt-4 space-y-4">
          <p>{t("my.cancelQuestion")}</p>
          {b.lateCancelNow && <Notice>{t("my.lateCancelWarning", { count: b.lateCancelHours })}</Notice>}
          <Button className="w-full" variant="danger" onClick={() => cancel.mutate({ token })} disabled={cancel.isPending}>
            {t("my.cancelConfirm")}
          </Button>
          <Button variant="ghost" className="w-full" onClick={() => setMode("view")}>
            {t("my.keep")}
          </Button>
        </Card>
      )}

      {mode === "reschedule" && (
        <div className="mt-4">
          <SectionTitle>{t("my.newTime")}</SectionTitle>
          <fieldset className="no-scrollbar -mx-4 flex min-w-0 gap-2 overflow-x-auto px-4 pb-2 sm:-mx-6 sm:px-6" aria-label={t("my.newTime")}>
            {days.map((d) => (
              <Chip key={d} active={d === activeDay} onClick={() => pickDay(d)}>
                {fmtDay(d, lang)}
              </Chip>
            ))}
          </fieldset>
          {slots.isPending ? (
            <Spinner />
          ) : slots.isError ? (
            <Notice kind="error">{trpcErrorCode(slots.error) === "TOO_MANY_REQUESTS" ? t("book.tooMany") : t("book.slotsError")}</Notice>
          ) : (slots.data ?? []).length === 0 ? (
            <Notice>{t("book.noSlots")}</Notice>
          ) : (
            <div className="mt-2 flex flex-wrap gap-2">
              {(slots.data ?? []).map((x) => (
                <Chip key={x.start.toISOString()} active={picked?.getTime() === x.start.getTime()} onClick={() => setPicked(x.start)}>
                  {fmtTime(x.start, tz, lang)}
                </Chip>
              ))}
            </div>
          )}
          <div className="mt-4 space-y-2">
            <Button className="w-full" disabled={!picked || reschedule.isPending} onClick={() => picked && reschedule.mutate({ token, start: picked })}>
              {picked ? t("my.moveTo", { time: `${fmtDay(dayKey(picked, tz), lang)} ${fmtTime(picked, tz, lang)}` }) : t("my.pickTime")}
            </Button>
            <Button variant="ghost" className="w-full" onClick={() => setMode("view")}>
              {t("common.cancel")}
            </Button>
          </div>
        </div>
      )}

      <Link to={`/book/${b.salon.slug}`} className="mt-8 block text-center text-sm font-medium text-brand underline-offset-2 hover:underline">
        {t("my.bookAnother")}
      </Link>
    </PublicShell>
  );
}
